import { ChangeEvent, useContext, VFC } from "react";
// Libraries
import { Formik } from "formik";
import * as Yup from "yup";
import RadioGroup from "@material-ui/core/RadioGroup";
import FormControlLabel from "@material-ui/core/FormControlLabel";
import Radio from "@material-ui/core/Radio";
import MaskedInput from "react-text-mask";
import { useMutation } from "@apollo/client";
// Utils
import { cphNumber } from "helpers/regex";
import { useGetCountries, useGetCurrentBusinessUnit } from "hooks";
import { MarkOffFarmValues } from "components/MyLivestock/Animals/AnimalBulkActions/ConfirmMarkOffFarmModal";
import { CommonContext } from "config/commonProvider";
import { getBUFromLocalStorage } from "helpers/storage";
import { formatAddressString } from "../AddressListTable";
// API
import { ContactContactType } from "api/graphql-global-types";
import { GET_ADDRESS_BOOK_LIST } from "api/AddressBook/queries";
import { GetContactList, GetContactListVariables } from "api/AddressBook/types/GetContactList";
import { CreateContact, CreateContactVariables } from "api/AddressBook/types/CreateContact";
import { CREATE_CONTACT } from "api/AddressBook/mutations";
// Components
import { handleEnterPress, handleErrorCheck } from "helpers/general";
import { FieldText, Flex, Field, Spacer, FieldSelect, Text, Button } from "components";
import { BaseInput } from "components/Common/Field/BaseInput";
import { useHoldingInfo } from "@breedr/utils";
// Resources
import styles from "pages/Settings/AddressBook/CreateEditContactAddress/styles.module.scss";

export interface AddAddressFormValues {
  contact?: string;
  name: string;
  organisationName: string;
  addressLine1: string;
  addressLine2: string;
  farmId: string;
  city: string;
  country: string;
  postcode: string;
  countryCode: string;
  phoneNumber: string;
  email: string;
  contactType: ContactContactType.FARM | ContactContactType.OTHER;
  notes: string;
}

type Props = {
  onToggleShowForm: (value: boolean) => void;
  onSubmitMarkOffFarm?: (values: MarkOffFarmValues) => void;
  onSubmitAddPreviousKeeper?: (values: AddAddressFormValues) => void;
  deliveryDate?: Date;
  isLoading: boolean;
  isPreviousKeeper?: boolean;
  initialValues?: AddAddressFormValues;
};

export const initialValuesDefault = {
  contact: "",
  name: "",
  organisationName: "",
  addressLine1: "",
  addressLine2: "",
  farmId: "",
  city: "",
  country: "",
  postcode: "",
  countryCode: "",
  phoneNumber: "",
  email: "",
  contactType: ContactContactType.FARM,
  notes: "",
};

const radioButtonClasses = { root: styles.root, checked: styles.checked };

export const NewContactModalContent: VFC<Props> = ({
  onToggleShowForm,
  onSubmitMarkOffFarm,
  onSubmitAddPreviousKeeper,
  deliveryDate,
  isLoading,
  isPreviousKeeper = false,
  initialValues,
}) => {
  const { data: countries } = useGetCountries();
  const [createContact, { loading: createContactLoading }] = useMutation<CreateContact, CreateContactVariables>(
    CREATE_CONTACT,
  );

  const { showNotification } = useContext(CommonContext);

  const filteredCountries = countries?.countries?.map((country) => {
    return {
      key: country?.id,
      label: country?.name,
      value: country?.id,
    };
  });

  const { regulator, country } = useGetCurrentBusinessUnit();
  //@ts-expect-error
  const { HoldingIDInitialism, HoldingIDRegex, HoldingIDNumberMaskWeb } = useHoldingInfo(country?.isoAlpha2);
  const isScotMoves = regulator?.slug === "SCOTMOVES";

  const label = isPreviousKeeper ? "Previous Keeper" : "Destination";
  const CPHlabel = isPreviousKeeper ? `${HoldingIDInitialism} Number` : `Destination ${HoldingIDInitialism} number`;

  const baseValidationSchema = Yup.object().shape(
    {
      name: Yup.string().required("Name is required!"),
      contactType: Yup.string().required("Contact type is required!"),
      farmId: Yup.string().when("addressLine1", {
        is: (addressLine1) => !addressLine1,
        then: Yup.string()
          .required(`Either a ${HoldingIDInitialism} number or address is required`)
          .matches(HoldingIDRegex, " "),
        otherwise: Yup.string(),
      }),
      addressLine1: Yup.string().when("farmId", {
        is: (farmId) => !farmId,
        then: Yup.string()
          .required(`Either a ${HoldingIDInitialism} number or address is required`)
          .when("contactType", {
            is: ContactContactType.OTHER,
            then: Yup.string().required("Address is required"),
          }),
        otherwise: Yup.string(),
      }),
      country: Yup.string().when(["farmId", "addressLine1"], {
        is: (farmId, addressLine1) => !farmId && !addressLine1,
        then: Yup.string().required(" "),
        otherwise: Yup.string(),
      }),
      email: Yup.string().email("E-mail is not valid!"),
    },
    [
      ["farmId", "addressLine1"],
      ["country", "farmId"],
    ],
  );

  const validationSchemaContactRequest = isScotMoves
    ? baseValidationSchema.shape({
        farmId: Yup.string().required("CPH/EC number is required").matches(cphNumber, " "),
        addressLine1: Yup.string(),
      })
    : baseValidationSchema;

  const handleSubmitButtonClick = async (props): Promise<void> => {
    const businessUnit = getBUFromLocalStorage();
    const input = {
      ...props,
      businessUnit,
      country: props.country || null,
    };
    delete input.countryCode;
    delete input.notes;
    delete input.contact;
    try {
      if (!isPreviousKeeper || (isPreviousKeeper && props.contact.length === 0)) {
        const { data } = await createContact({
          variables: {
            input,
          },
          update: (cache, { data }) => {
            if (data?.createContact?.contact) {
              const previousData = cache.readQuery<GetContactList, GetContactListVariables>({
                query: GET_ADDRESS_BOOK_LIST,
                variables: {
                  businessUnit,
                },
              });

              const updatedContactsList = [...(previousData?.contacts || []), data?.createContact?.contact];

              cache.writeQuery({
                query: GET_ADDRESS_BOOK_LIST,
                variables: {
                  businessUnit,
                },
                data: {
                  contacts: updatedContactsList,
                },
              });
            }
          },
        });
        const success = !data?.createContact?.errors;

        if (!success) {
          showNotification({
            variant: "error",
            message: "Error creating contact",
          });
        } else {
          showNotification({
            message: "Contact successfully created",
          });
          if (isPreviousKeeper && onSubmitAddPreviousKeeper) {
            onSubmitAddPreviousKeeper(input);
          } else if (onSubmitMarkOffFarm) {
            onSubmitMarkOffFarm({
              ...props,
              deliveryDate,
              destinationAddress: formatAddressString(props),
              destinationCPHNumber: props.farmId,
              destinationName: props.name,
              destinationPostcode: props.postcode,
              destinationEmail: props.email,
            });
          }
        }
      } else if (onSubmitAddPreviousKeeper) {
        onSubmitAddPreviousKeeper(input);
      }
    } catch (e) {
      showNotification({
        variant: "error",
        message: "Something went wrong! Please try again later.",
      });
    }
  };

  const handleCancelButtonClick = (): void => {
    onToggleShowForm(false);
  };

  return (
    <>
      <Formik
        enableReinitialize
        onSubmit={handleSubmitButtonClick}
        initialValues={initialValues ? initialValues : initialValuesDefault}
        validateOnChange={false}
        validationSchema={validationSchemaContactRequest}
      >
        {({ values, errors, handleChange, setFieldValue, handleSubmit, setErrors }): JSX.Element => {
          const handleContactTypeChange = (e: ChangeEvent<unknown>, value: string): void => {
            setFieldValue("contactType", value);
            setErrors({});
          };
          const handleRegionChange = (e: ChangeEvent<HTMLInputElement>): void => {
            const countryCode = countries?.countries?.find((item) => item?.id === e.target.value)?.code?.toLowerCase();
            setFieldValue("country", e.target.value);
            setFieldValue("countryCode", countryCode);
            setFieldValue("farmId", "");
          };

          const isFarm = values.contactType === ContactContactType.FARM;

          return (
            <>
              <Flex container>
                <Flex item xs={12} l={6} xl={4}>
                  {!isPreviousKeeper && (
                    <Field error={errors.contactType} labelFor="contactType" label="Contact Type">
                      <RadioGroup
                        aria-label="Contact type"
                        name="contactType"
                        value={values.contactType}
                        onChange={handleContactTypeChange}
                        row
                      >
                        <FormControlLabel
                          value={ContactContactType.FARM}
                          control={<Radio classes={radioButtonClasses} />}
                          label="Farm"
                        />
                        {!isScotMoves && (
                          <FormControlLabel
                            value={ContactContactType.OTHER}
                            control={<Radio classes={radioButtonClasses} />}
                            label="Other"
                          />
                        )}
                      </RadioGroup>
                    </Field>
                  )}
                  <FieldText
                    label={`${label} Name`}
                    error={handleErrorCheck(errors.name)}
                    inputProps={{
                      name: "name",
                      placeholder: "Enter name",
                      value: values.name,
                      onChange: handleChange,
                      disabled: !!values.contact,
                      onKeyPress: (e): void => handleEnterPress(e, handleSubmit),
                    }}
                  />
                  <FieldSelect
                    label={`${label} Country`}
                    error={handleErrorCheck(errors.country)}
                    inputProps={{
                      name: "country",
                      placeholder: "Select country",
                      value: values.country,
                      options: filteredCountries,
                      disabled: !!values.contact,
                      onChange: handleRegionChange,
                    }}
                  />
                  {isFarm && (
                    <MaskedInput
                      label={CPHlabel}
                      mask={HoldingIDNumberMaskWeb}
                      name="farmId"
                      value={values.farmId}
                      onChange={handleChange}
                      disabled={!!values.contact}
                      render={(ref, props): JSX.Element => (
                        <Field error={errors.farmId} labelFor="farmId" {...props}>
                          <BaseInput hasError={!!errors.farmId} innerRef={ref} {...props} />
                        </Field>
                      )}
                    />
                  )}

                  <Text>{`${label} address`}</Text>
                  <FieldText
                    error={handleErrorCheck(errors.addressLine1)}
                    inputProps={{
                      name: "addressLine1",
                      placeholder: "Address Line 1",
                      value: values.addressLine1,
                      disabled: !!values.contact,
                      onChange: handleChange,
                      onKeyPress: (e): void => handleEnterPress(e, handleSubmit),
                    }}
                  />
                  <FieldText
                    error={handleErrorCheck(errors.addressLine2)}
                    inputProps={{
                      name: "addressLine2",
                      placeholder: "Address Line 2",
                      value: values.addressLine2,
                      disabled: !!values.contact,
                      onChange: handleChange,
                      onKeyPress: (e): void => handleEnterPress(e, handleSubmit),
                    }}
                  />
                  <FieldText
                    error={handleErrorCheck(errors.city)}
                    inputProps={{
                      name: "city",
                      placeholder: "Town",
                      value: values.city,
                      disabled: !!values.contact,
                      onChange: handleChange,
                      onKeyPress: (e): void => handleEnterPress(e, handleSubmit),
                    }}
                  />
                  <FieldText
                    error={handleErrorCheck(errors.postcode)}
                    inputProps={{
                      name: "postcode",
                      placeholder: "Postcode",
                      value: values.postcode,
                      disabled: !!values.contact,
                      onChange: handleChange,
                      onKeyPress: (e): void => handleEnterPress(e, handleSubmit),
                    }}
                  />
                  <FieldText
                    label={`${label} Email Address`}
                    error={handleErrorCheck(errors.email)}
                    inputProps={{
                      name: "email",
                      placeholder: "Enter Email Address",
                      value: values.email,
                      disabled: !!values.contact,
                      onChange: handleChange,
                      onKeyPress: (e): void => handleEnterPress(e, handleSubmit),
                    }}
                  />
                  {!isPreviousKeeper && (
                    <FieldText
                      label="Notes (Optional)"
                      error={handleErrorCheck(errors.notes)}
                      inputProps={{
                        name: "notes",
                        placeholder: "Enter Notes",
                        value: values.notes,
                        onChange: handleChange,
                        onKeyPress: (e): void => handleEnterPress(e, handleSubmit),
                      }}
                    />
                  )}
                  <Spacer baselineHeight={2} />
                  <Flex container containerJustifyContent="flex-end">
                    <Flex item>
                      <Button caption="Cancel" colour="grey" variant="hollow" onClick={handleCancelButtonClick} />
                    </Flex>
                    <Flex item itemGutter>
                      <Button
                        caption="Confirm"
                        onClick={handleSubmit}
                        requesting={isLoading}
                        disabled={isLoading || createContactLoading}
                      />
                    </Flex>
                  </Flex>
                  <Spacer baselineHeight={1} />
                </Flex>
              </Flex>
            </>
          );
        }}
      </Formik>
    </>
  );
};
